import { FC, useState, useEffect } from 'react';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useGlobalTour } from '../contexts/TourContext';
import { useTranslatedSteps } from './stepsTour';
import {
  Box,
  TextField,
  Button,
  Alert,
  Paper,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { onboarding } from '../services/api';
import { useTranslation } from '../contexts/TranslationContext';
import { enqueueSnackbar } from 'notistack';
import VideoProvider from '../components/VideoProvider';
import { videoUrls, videoTitles } from '../utils/videoUrls';

interface BusinessForm {
  businessName: string;
  businessType: string;
  businessSize: string;
  businessDescription: string;
  business_address: string;
  description?: string;
  service_type: string;
  service_description: string;
  target_audience: string;
}

const SERVICE_TYPE_KEYS = ['consultation', 'training', 'service', 'other'] as const;

export const BusinessContent: FC = () => {
  const { t } = useTranslation();
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const [formData, setFormData] = useState<BusinessForm>({
    businessName: '',
    businessType: '',
    businessSize: '',
    businessDescription: '',
    business_address: '',
    description: '',
    service_type: '',
    service_description: '',
    target_audience: '',
  });
  const [editFormData, setEditFormData] = useState<BusinessForm>({
    businessName: '',
    businessType: '',
    businessSize: '',
    businessDescription: '',
    business_address: '',
    description: '',
    service_type: '',
    service_description: '',
    target_audience: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  let flag = false;
  let flag_incomplete = false;
  
  // Register tour function with global tour context
  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        const [businessResponse, serviceResponse] = await Promise.all([
          onboarding.getBusinessInfo(),
          onboarding.getServiceInfo(),
        ]);
        
        setFormData({
          businessName: businessResponse.data.business_name,
          businessType: businessResponse.data.business_type,
          businessSize: businessResponse.data.business_size,
          businessDescription: businessResponse.data.business_description,
          business_address: businessResponse.data.business_address,
          description: businessResponse.data.description || '',
          service_type: serviceResponse.data.service_type,
          service_description: serviceResponse.data.service_description,
          target_audience: serviceResponse.data.target_audience,
        });
      } catch (err: any) {
        console.error('Error fetching business data:', err);
        if (err.response?.status === 404) {
          setNotFound(true);
          enqueueSnackbar(t('business.error.notFound'), {
            variant: 'error',
            autoHideDuration: 4000,
            style: {
              padding: '10px',
              marginTop: '20px',
            },
          });
        } else if (err.response?.status === 400) {
          if (!flag_incomplete) {
            enqueueSnackbar(t('business.error.incomplete'), { 
              variant: 'error',
              autoHideDuration: 4000,
              style: {
                padding: '10px',
                marginTop: '20px',
              },
            });
            flag_incomplete = true;
          }
        } else {
          if (!flag) {
            enqueueSnackbar(t('business.error.sessionExpired'), { 
              variant: 'error',
              autoHideDuration: 4000,
              style: {
                padding: '10px',
                marginTop: '20px', // Add margin top
              },
            });
            flag = true;
          }
          setTimeout(() => {
            localStorage.removeItem('token');
            window.location.href = '/auth/auth';
          }, 3000);
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchBusinessData();
  }, [t]);

  const handleOpenModal = () => {
    setEditFormData({ ...formData });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveBusiness = async () => {
    try {
      setLoading(true);
      setError('');
      
      await Promise.all([
        onboarding.saveBusinessInfo({
          business_name: editFormData.businessName.trim(),
          business_type: editFormData.businessType.trim(),
          business_size: editFormData.businessSize.trim(),
          business_description: editFormData.businessDescription.trim(),
          business_address: editFormData.business_address.trim(),
          description: editFormData.description?.trim() || "",
        }),
        onboarding.saveServiceInfo({
          service_type: editFormData.service_type.trim(),
          service_description: editFormData.service_description.trim(),
          target_audience: editFormData.target_audience.trim(),
        }),
      ]);

      // Update local state
      setFormData({ ...editFormData });

      enqueueSnackbar(t('business.success'), { 
        variant: 'success',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });

      handleCloseModal();
    } catch (err: any) {
      if (err.response?.status === 400) {
        enqueueSnackbar(t('business.error.incomplete'), { 
          variant: 'error',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
      } else if (err.response?.status === 403) {
        enqueueSnackbar(t('business.error.forbidden'), {
          variant: 'error',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
      } else if (err.response?.status === 500) {
        enqueueSnackbar(t('business.error.saveError'), { 
          variant: 'error',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
      } else {
        let error_mes = "";
        if (err.response?.data?.detail?.[0]?.msg === "String should have at least 2 characters") {
          error_mes = t("business.error.business_name_error");
        } else if (err.response?.data?.detail?.[0]?.msg === "String should have at least 5 characters") {
          error_mes = t("business.error.business_des_error");
        } else if (err.response?.data?.detail?.[0]?.msg === "String should have at least 6 characters") {
          error_mes = t("business.error.address_error");
        }
        enqueueSnackbar(error_mes || t('business.error.saveError'), { 
          variant: 'error',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
      }
      console.error('Error saving business info:', err);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Alert severity="warning">
            {error}
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{borderRadius: '10px', overflow:'hidden' , position:'relative'}} className="first-step">
        <Box sx={{height: '110px', backgroundImage: 'url(/images/backProfile2.jpg)', backgroundSize: 'cover'}} className='back_profile_image'>
        </Box>
        <Box sx={{position:'absolute', top: '30px', left:'40px' , overflow:'hidden', zIndex: 1000, display:'flex', alignItems:'center', gap:'30px'}} className='profile_box_profile'>
          <p style={{fontSize: '24px', fontWeight: 'bold', textAlign: 'center', paddingTop:'10px', color: 'white'}} className='text_business_name'>{formData.businessName}</p>
        </Box>
        <Box sx={{height:'100%' , p:{xs:3, md:4}, pt:'30px', display:'flex', flexDirection:'column'}}>
          {/* <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              {t('business.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('business.businessName')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              direction: 'ltr',
              textAlign: 'left'
            }}>
              <Typography variant="body1">
                {formData.businessName || '-'}
              </Typography>
            </Box>
          </Box> */}

          <Box sx={{ mb: {xs:2, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('business.businessType')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              direction: 'ltr',
              textAlign: 'left'
            }}>
              <Typography variant="body1">
                {formData.businessType || '-'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: {xs:2, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('service.serviceType')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1">
                {formData.service_type ? t(`service.types.${formData.service_type}`) : '-'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: {xs:2, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('business.businessSize')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1">
                {formData.businessSize || '-'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: {xs:2, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('business.address')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1">
                {formData.business_address || '-'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: {xs:2, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('business.businessDescription')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1">
                {formData.businessDescription || '-'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: {xs:2, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('service.serviceDescription')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1">
                {formData.service_description || '-'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: {xs:2, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('service.targetAudience')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1">
                {formData.target_audience || '-'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{alignSelf:'end' , marginTop:{xs:1, md:2}}}>
            <Button
              variant="contained"
              onClick={handleOpenModal}
              className="business-edit-button"
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '0.875rem',
                px: { xs: 2, sm: 3 },
                py: { xs: 0.9, sm: 1 },
                '@media (max-width: 600px)': {
                  fontSize: '0.85rem !important',
                  width: 'auto !important',
                  minWidth: 'auto !important',
                },
              }}
            >
              {t('business.edit_modal_button')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Edit Business Modal */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {t('business.edit_modal_title')}
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            fullWidth
            label={t('business.businessName')}
            name="businessName"
            value={editFormData.businessName}
            onChange={handleEditChange}
            margin="normal"
            required
            helperText={t('business.businessNameHelper')}
          />

          <TextField
            fullWidth
            label={t('business.businessType')}
            name="businessType"
            value={editFormData.businessType}
            onChange={handleEditChange}
            margin="normal"
            required
            helperText={t('business.businessTypeHelper')}
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>{t('service.serviceType')}</InputLabel>
            <Select
              name="service_type"
              value={editFormData.service_type}
              onChange={handleEditSelectChange}
              label={t('service.serviceType')}
            >
              {SERVICE_TYPE_KEYS.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`service.types.${type}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" required>
            <InputLabel>{t('business.businessSize')}</InputLabel>
            <Select
              name="businessSize"
              value={editFormData.businessSize}
              onChange={handleEditSelectChange}
              label={t('business.businessSize')}
            >
              <MenuItem value="کوچک">{t('business.sizes.small')}</MenuItem>
              <MenuItem value="متوسط">{t('business.sizes.medium')}</MenuItem>
              <MenuItem value="بزرگ">{t('business.sizes.large')}</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('business.businessSizeHelper')}
            </Typography>
          </FormControl>

          <TextField
            fullWidth
            label={t('business.address')}
            name="business_address"
            value={editFormData.business_address}
            onChange={handleEditChange}
            margin="normal"
            required
            helperText={t('business.addressHelper')}
          />

          <TextField
            fullWidth
            label={t('business.businessDescription')}
            name="businessDescription"
            value={editFormData.businessDescription}
            onChange={handleEditChange}
            margin="normal"
            multiline
            rows={4}
            required
            helperText={t('business.businessDescriptionHelper')}
          />

          <TextField
            fullWidth
            label={t('service.serviceDescription')}
            name="service_description"
            value={editFormData.service_description}
            onChange={handleEditChange}
            margin="normal"
            multiline
            rows={4}
            required
            helperText={t('service.serviceDescriptionHelper')}
          />

          <TextField
            fullWidth
            label={t('service.targetAudience')}
            name="target_audience"
            value={editFormData.target_audience}
            onChange={handleEditChange}
            margin="normal"
            multiline
            rows={3}
            required
            helperText={t('service.targetAudienceHelper')}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleCloseModal} 
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none',
              minWidth: 100
            }}
          >
            {t('business.cancel')}
          </Button>
          <Button 
            onClick={handleSaveBusiness}
            variant="contained"
            disabled={loading || !editFormData.businessName.trim() || !editFormData.businessType.trim() || !editFormData.businessSize.trim() || !editFormData.business_address.trim() || !editFormData.businessDescription.trim() || !editFormData.service_type.trim() || !editFormData.service_description.trim() || !editFormData.target_audience.trim()}
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none',
              minWidth: 100
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('business.save_modal')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Business: FC = () => {
  return (
    <TourProvider steps={useTranslatedSteps().steps_business}>
      <VideoProvider videoUrl={videoUrls.business} videoTitle={videoTitles.business}>
        <BusinessContent />
      </VideoProvider>
    </TourProvider>
  );
};

export default Business; 